import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { IMG, LINKS } from "../../data/site";
import { scrollToId } from "../../lib/img";
import { CTA, Photo } from "./primitives";

const SLIDES = [IMG.cliffside, IMG.coastline, IMG.golden];
const SLIDE_MS = 7500;

const Slide = ({ img, active, first, reduce }) => (
  <motion.div className="absolute inset-0" initial={false} animate={{ opacity: active ? 1 : 0 }} transition={{ duration: reduce ? 0 : 2, ease: "easeInOut" }}>
    <motion.div
      className="h-full w-full"
      initial={reduce || !first ? false : { scale: 1.12 }}
      animate={{ scale: reduce ? 1 : active ? 1 : 1.12 }}
      transition={{ duration: active ? 10 : 0, delay: active ? 0 : 2, ease: "linear" }}
    >
      <Photo img={img} eager={first} sizes="100vw" className="h-full w-full object-cover" aria-hidden={!active} data-testid={`hero-slide-${img === IMG.cliffside ? "cliffside" : img === IMG.coastline ? "coastline" : "golden"}`} />
    </motion.div>
  </motion.div>
);

export default function Hero() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const [ready, setReady] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["0%", "-24%"]);
  const fade = useTransform(scrollYProgress, [0, 0.65], reduce ? [1, 1] : [1, 0]);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 1800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (reduce || !ready) return;
    const t = setTimeout(() => setIdx((i) => (i + 1) % SLIDES.length), SLIDE_MS);
    return () => clearTimeout(t);
  }, [idx, reduce, ready]);

  const enter = (d) => (reduce ? {} : { initial: { opacity: 0, y: 26 }, animate: { opacity: 1, y: 0 }, transition: { delay: d, duration: 1.3, ease: [0.22, 1, 0.36, 1] } });

  return (
    <section ref={ref} id="top" aria-label="Welcome" data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden bg-ink">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        {SLIDES.map((img, i) => (i === 0 || ready) && <Slide key={img.p} img={img} active={i === idx} first={i === 0} reduce={reduce} />)}
      </motion.div>
      <div aria-hidden className="absolute inset-0 bg-[linear-gradient(to_top,rgba(28,24,20,0.82)_0%,rgba(28,24,20,0.35)_42%,rgba(28,24,20,0.05)_70%)]" />
      <div aria-hidden className="absolute inset-0 hidden bg-[linear-gradient(to_right,rgba(28,24,20,0.6)_0%,rgba(28,24,20,0.25)_40%,transparent_62%)] md:block" />

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-6 pb-28 sm:px-8 md:pb-32 lg:px-12">
        <motion.p {...enter(0.2)} className="text-[11px] font-semibold uppercase tracking-[0.3em] text-ivory/90 [text-shadow:0_1px_12px_rgba(0,0,0,0.35)]">
          Yoga · Breathwork · Massage · Meditation · Hypnotherapy
        </motion.p>
        <motion.h1 {...enter(0.35)} data-testid="hero-title" className="mt-5 max-w-4xl font-display text-[3.1rem] font-light leading-[0.98] tracking-[-0.01em] text-ivory sm:text-6xl lg:text-[5.5rem]">
          Come back into <em className="font-normal italic text-[#F6D2BC] [text-shadow:0_1px_14px_rgba(20,16,12,0.5)]">your body.</em>
        </motion.h1>
        <motion.p {...enter(0.55)} data-testid="hero-subtitle" className="mt-6 max-w-xl text-base leading-relaxed text-ivory/90 [text-shadow:0_1px_10px_rgba(20,16,12,0.45)] sm:text-lg">
          When you're connected to your breath, your body and your awareness… everything changes.
        </motion.p>
        <motion.div {...enter(0.7)} className="mt-9 flex flex-col gap-3 sm:flex-row">
          <CTA
            href="#paths"
            variant="ivory"
            testid="hero-explore-button"
            icon={false}
            onClick={(e) => {
              e.preventDefault();
              scrollToId("paths");
            }}
          >
            Choose your path
          </CTA>
          <CTA href={LINKS.contact} variant="outlineLight" testid="hero-book-button">
            Book a Session
          </CTA>
        </motion.div>
      </motion.div>

      <div className="absolute inset-x-0 bottom-6 z-10 mx-auto flex max-w-7xl items-center justify-between px-6 sm:px-8 lg:px-12">
        <span className="hidden text-[11px] uppercase tracking-[0.3em] text-ivory/60 md:block">Scroll to begin</span>
        <div className="flex items-center gap-1" role="group" aria-label="Hero photos">
          {SLIDES.map((img, i) => (
            <button
              key={img.p}
              type="button"
              onClick={() => {
                setReady(true);
                setIdx(i);
              }}
              aria-label={`Show photo ${i + 1} of ${SLIDES.length}`}
              aria-pressed={i === idx}
              data-testid={`hero-dot-${i + 1}`}
              className="flex h-11 w-11 items-center justify-center"
            >
              <span className={`block h-px transition-[width,background-color] duration-700 ${i === idx ? "w-8 bg-ivory" : "w-4 bg-ivory/45"}`} />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
